// api.ts — TanStack Query hooks for the Inventory module (server state).
// All server data flows through here; components never fetch directly.
//
// Real-API mode: each hook resolves its mock path against api/paths.ts. If
// the endpoint is wired up (e.g. /products → /api/master-data/catalog/) the
// hook hits the real DRF route, unwraps the paginated envelope, and maps DTOs
// into UI shapes. If a path isn't mapped yet, the hook short-circuits to an
// empty array so the rest of the UI keeps rendering while we slice modules.
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch, apiFetchList } from '@/api/client';
import { resolvePath, resolveDetailPath, useRealApi } from '@/api/paths';
import {
  unwrapPage,
  snapshotToProduct,
  storeProductDtoToStoreProduct,
  storageBinToBin,
  batchStockToBatch,
  stockAllocationToAllocation,
  storeDtoToStore,
  productToCatalogPayload,
  binToStorageBinPayload,
} from '@/api/adapters';
import {
  adjustmentToInventoryUi,
  auditLogToEntry,
  ledgerToMovement,
  stockAlertToInventoryAlert,
  stocktakeToReconSession,
} from '@/api/stocksAdapters';
import { useStore } from '@/store/store';
import type {
  Product,
  Batch,
  Bin,
  Adjustment,
  Allocation,
  Movement,
  AuditEntry,
  Alert,
  StoreProduct,
} from '@/domain/types';

export const inventoryKeys = {
  products: (store: string) => ['inventory', 'products', store] as const,
  storeProducts: (store: string) => ['inventory', 'store-products', store] as const,
  product: (id: string) => ['inventory', 'product', id] as const,
  batches: (store: string) => ['inventory', 'batches', store] as const,
  bins: (store: string) => ['inventory', 'bins', store] as const,
  adjustments: (store: string) => ['inventory', 'adjustments', store] as const,
  movements: (store: string) => ['inventory', 'movements', store] as const,
  audit: () => ['inventory', 'audit'] as const,
  alerts: (store: string) => ['inventory', 'alerts', store] as const,
  allocations: () => ['inventory', 'allocations'] as const,
  stores: () => ['inventory', 'stores'] as const,
  reconSessions: () => ['inventory', 'recon-sessions'] as const,
};

/** Fetch a path that may be unmapped in real-API mode: returns [] when null. */
function useListQuery<TUi>(
  mockPath: string,
  key: readonly unknown[],
  opts: {
    query?: Record<string, string | number | boolean | undefined>;
    map?: (raw: unknown) => TUi[];
    staleTime?: number;
    maxPages?: number;
    enabled?: boolean;
  } = {},
) {
  const resolved = resolvePath(mockPath);
  return useQuery({
    queryKey: [...key, resolved?.path ?? 'noop'],
    enabled: resolved !== null && (opts.enabled ?? true),
    staleTime: opts.staleTime,
    queryFn: async () => {
      if (!resolved) return [] as TUi[];
      const data = await apiFetchList<unknown>(resolved.path, {
        query: opts.query,
        maxPages: opts.maxPages,
      });
      if (opts.map) return opts.map(data);
      return resolved.paginated ? (unwrapPage(data) as TUi[]) : (data as TUi[]);
    },
  });
}

export function useProducts() {
  const { store, can } = useStore();
  // Products catalog is global in real API mode (dm+d snapshots not store-scoped)
  const queryKey = useRealApi
    ? inventoryKeys.products('__global__')
    : inventoryKeys.products(store);
  return useListQuery<Product>('/products', queryKey, {
    query: useRealApi ? { page_size: 200 } : { store },
    // the dm+d directory is 17k rows — pickers only need the head, so one
    // page is deliberate (the Master Data catalogue has a real server pager)
    maxPages: 1,
    // mock rows are already UI-shaped; the snapshot adapter is real-API only
    map: useRealApi ? (raw) => unwrapPage(raw).map(snapshotToProduct) : undefined,
    // the catalogue payload is ~100 kB and changes rarely — cache it hard
    staleTime: 5 * 60 * 1000,
    // the catalogue is gated by `master.access` on the API — don't fetch it for roles that lack it
    // (e.g. pharmacist), which would otherwise 403 on every dashboard load.
    enabled: !useRealApi || can('master.access'),
  });
}

/**
 * Fetches aggregated inventory products for the currently selected store.
 * Returns products that have batch stock, with nested batch and bin groupings.
 */
export interface StoreProductFilters {
  controlled?: boolean;
  coldChain?: boolean;
}

export function useStoreProducts(
  groupBy: 'ampp' | 'vmpp' | 'vmp' = 'ampp',
  filters: StoreProductFilters = {},
  /** Explicit store override — flows that carry their own store selection
   *  (e.g. the quick-actions adjust picker under "All Stores") must query for
   *  THAT store, not the global topbar context. */
  storeIdOverride?: string,
) {
  const { store: contextStore } = useStore();
  const store = storeIdOverride ?? contextStore;
  const validId = /^[0-9a-f]{8}-/i.test(store) ? store : undefined;
  const { controlled, coldChain } = filters;
  return useListQuery<StoreProduct>(
    '/store-products',
    [...inventoryKeys.storeProducts(store), groupBy, controlled ?? false, coldChain ?? false],
    {
      query: {
        store_id: validId,
        group_by: groupBy,
        ...(controlled ? { controlled: 'true' } : {}),
        ...(coldChain ? { cold_chain: 'true' } : {}),
      },
      map: (raw) => (raw as unknown[]).map(storeProductDtoToStoreProduct),
      staleTime: 30_000,
      // The real endpoint REQUIRES store_id (400 otherwise) and has no all-stores
      // mode — never fire without a resolvable store. Consumers render their own
      // pick-a-store gate under 'All Stores'. Mock mode keeps the aggregate view.
      enabled: !useRealApi || !!validId,
    },
  );
}

export function useProduct(id: string | undefined) {
  // Send the active store so the catalogue read reflects THIS store's formulary (archive) overlay.
  const { store } = useStore();
  const storeId = store && store !== 'ALL' ? store : undefined;
  return useQuery({
    queryKey: [...inventoryKeys.product(id ?? ''), storeId ?? 'all'],
    queryFn: async () => {
      if (useRealApi) {
        const path = resolveDetailPath(`/products/${id}`);
        if (!path) return undefined;
        const raw = await apiFetch<unknown>(
          storeId ? `${path}?store_id=${encodeURIComponent(storeId)}` : path,
        );
        return snapshotToProduct(raw);
      }
      return apiFetch<Product>(`/products/${id}`);
    },
    enabled: !!id,
  });
}

/** Archive/activate a product in the ACTIVE store's formulary. Catalogue (dm+d/C+D) products are a
 *  shared read-only snapshot, so this writes the tenant's per-store TenantProduct overlay via the
 *  archive/activate action — NOT a PATCH on the snapshot (which 405s). */
export function useArchiveProduct() {
  const qc = useQueryClient();
  const { store } = useStore();
  return useMutation({
    mutationFn: ({ id, archive }: { id: string; archive: boolean }) => {
      const storeId = store && store !== 'ALL' ? store : undefined;
      if (!useRealApi) return apiFetch(`/products/${id}`, { method: 'PATCH', body: {} });
      const path = resolveDetailPath(`/products/${id}/${archive ? 'archive' : 'activate'}`);
      if (!path) return Promise.reject(new Error('Path not mapped'));
      const q = storeId ? `?store_id=${encodeURIComponent(storeId)}` : '';
      return apiFetch<{ id: string; is_active: boolean }>(`${path}${q}`, {
        method: 'POST',
        body: {},
      });
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: inventoryKeys.product(id) });
      qc.invalidateQueries({ queryKey: inventoryKeys.storeProducts(store) });
      qc.invalidateQueries({ queryKey: inventoryKeys.products(store) });
      qc.invalidateQueries({ queryKey: inventoryKeys.alerts(store) });
    },
  });
}

/** Set a product's per-store reorder min/max. Reorder levels have no per-product home on the shared
 *  read-only catalogue, so this posts to the reorder-levels action (tenant per-store overlay) rather
 *  than PATCHing the snapshot (which 405s). */
export function useSetReorderLevels() {
  const qc = useQueryClient();
  const { store } = useStore();
  return useMutation({
    mutationFn: ({ id, min, max }: { id: string; min: number; max: number }) => {
      const storeId = store && store !== 'ALL' ? store : undefined;
      if (!useRealApi) return apiFetch(`/products/${id}`, { method: 'PATCH', body: { min, max } });
      const path = resolveDetailPath(`/products/${id}/reorder-levels`);
      if (!path) return Promise.reject(new Error('Path not mapped'));
      const q = storeId ? `?store_id=${encodeURIComponent(storeId)}` : '';
      return apiFetch<{ reorder_min: number; reorder_max: number }>(`${path}${q}`, {
        method: 'POST',
        body: { reorder_min: min, reorder_max: max },
      });
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: inventoryKeys.product(id) });
      qc.invalidateQueries({ queryKey: inventoryKeys.storeProducts(store) });
      qc.invalidateQueries({ queryKey: inventoryKeys.products(store) });
      qc.invalidateQueries({ queryKey: inventoryKeys.alerts(store) });
    },
  });
}

/** Batches scoped to the active store, or to `storeId` when given (e.g. the
 *  network-stock cell drill inspects OTHER stores' batches). */
export function useBatches(storeId?: string, opts: { sku?: string; enabled?: boolean } = {}) {
  const { store } = useStore();
  const target = storeId ?? store;
  const validId = /^[0-9a-f]{8}-/i.test(target) ? target : undefined;
  return useListQuery<Batch>(
    '/batches',
    opts.sku ? [...inventoryKeys.batches(target), opts.sku] : inventoryKeys.batches(target),
    {
      query: { store_id: validId, sku: opts.sku },
      map: useRealApi ? (raw) => unwrapPage(raw).map(batchStockToBatch) : undefined,
      enabled: opts.enabled,
    },
  );
}

/** Bins of `storeId` (default: the active store). The bins API is gated by `master.access`. */
export function useBins(opts: { storeId?: string; enabled?: boolean } = {}) {
  const { store, can } = useStore();
  const target = opts.storeId ?? store;
  const validId = /^[0-9a-f]{8}-/i.test(target) ? target : undefined;
  return useListQuery<Bin>('/bins', inventoryKeys.bins(target), {
    query: useRealApi ? { store_id: validId } : { store: target },
    map: useRealApi ? (raw) => unwrapPage(raw).map(storageBinToBin) : undefined,
    // IMP-N16: delivery verifiers (pharmacist, IM) may read bins to choose a put-away bin; the
    // backend lets the inward-verify capability list them (writes still need master.edit).
    enabled:
      (opts.enabled ?? true) && (!useRealApi || can('master.access') || can('stk.inward.approve')),
  });
}

export function useAdjustments() {
  const { store } = useStore();
  return useListQuery<Adjustment>('/adjustments', inventoryKeys.adjustments(store), {
    query: useRealApi ? undefined : { store },
    map: useRealApi ? (raw) => unwrapPage(raw).map(adjustmentToInventoryUi) : undefined,
  });
}

export function useMovements() {
  const { store } = useStore();
  return useListQuery<Movement>('/movements', inventoryKeys.movements(store), {
    query: useRealApi ? undefined : { store },
    map: useRealApi ? (raw) => unwrapPage(raw).map(ledgerToMovement) : undefined,
  });
}

export function useAudit() {
  return useListQuery<AuditEntry>('/audit', inventoryKeys.audit(), {
    map: useRealApi ? (raw) => unwrapPage(raw).map(auditLogToEntry) : undefined,
  });
}

/**
 * One record's audit trail: the server's `reference` filter returns the entity's own events plus
 * any event naming it. Fetched only when `enabled` (e.g. the audit panel is open) and only for
 * roles holding `audit.read` — everyone else would just get a 403.
 */
export function useEntityAudit(reference: string | undefined, enabled: boolean) {
  const { can } = useStore();
  return useListQuery<AuditEntry>('/audit', [...inventoryKeys.audit(), reference ?? ''], {
    query: useRealApi ? { reference } : undefined,
    map: useRealApi
      ? (raw) => unwrapPage(raw).map(auditLogToEntry)
      : (raw) => unwrapPage<AuditEntry>(raw).filter((a) => a.entity.includes(reference ?? '')),
    enabled: enabled && !!reference && (!useRealApi || can('audit.read')),
  });
}

export function useAllocations() {
  return useListQuery<Allocation>('/allocations', inventoryKeys.allocations(), {
    map: useRealApi ? (raw) => unwrapPage(raw).map(stockAllocationToAllocation) : undefined,
  });
}

export function useAlerts() {
  const { store } = useStore();
  // Only pass store_id if it looks like a valid UUID (not 'ALL' or a mock id)
  const isValidUuid = /^[0-9a-f]{8}-/i.test(store);
  return useListQuery<Alert>('/alerts', inventoryKeys.alerts(store), {
    query: useRealApi ? { store_id: isValidUuid ? store : undefined } : { store },
    map: useRealApi ? (raw) => unwrapPage(raw).map(stockAlertToInventoryAlert) : undefined,
  });
}

/** Coverage of a reconciliation: whole store vs a scoped subset. */
export type ReconSessionType = 'full_cycle' | 'partial';

export interface ReconSession {
  id: string;
  uuid?: string;
  scope: string;
  sessionType: ReconSessionType;
  periodStart: string | null;
  periodEnd: string | null;
  store: string;
  snapCount: number;
  status: 'In Progress' | 'Closed';
  started: string;
  closed: string | null;
}

export function useReconSessions() {
  return useListQuery<ReconSession>('/reconciliation/sessions', inventoryKeys.reconSessions(), {
    map: useRealApi ? (raw) => unwrapPage(raw).map(stocktakeToReconSession) : undefined,
  });
}

/** Start a session. Sends the exact in-scope batch ids so the backend freezes a snapshot (one
 *  count row per batch with the current on-hand as the expected qty). Returns the raw session
 *  including its frozen `counts`. */
export function useStartReconSession() {
  const qc = useQueryClient();
  const { store } = useStore();
  return useMutation({
    mutationFn: (session: {
      scope?: string;
      store?: string;
      batchIds?: string[];
      sessionType?: ReconSessionType;
      periodStart?: string | null;
      periodEnd?: string | null;
    }) => {
      const storeId = session.store && session.store !== 'ALL' ? session.store : store;
      if (useRealApi) {
        if (storeId === 'ALL') {
          return Promise.reject(new Error('Select a store to start a count'));
        }
        return apiFetch<RawReconSession>('/api/inventory/stocktakes/', {
          method: 'POST',
          body: {
            store_id: storeId,
            reference: `REC-${Date.now()}`,
            scope: session.scope ?? '',
            session_type: session.sessionType ?? 'partial',
            period_start: session.periodStart ?? null,
            period_end: session.periodEnd ?? null,
            // A full-cycle count ignores this server-side and snapshots the whole store.
            batch_ids: session.batchIds ?? [],
          },
        });
      }
      return apiFetch<RawReconSession>('/reconciliation/sessions', {
        method: 'POST',
        body: { store: storeId, scope: session.scope },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: inventoryKeys.reconSessions() }),
  });
}

/** Fetch one session's raw detail (frozen counts) — used to resume an in-progress session. */
export function fetchReconSessionDetail(id: string): Promise<RawReconSession> {
  const path = useRealApi ? `/api/inventory/stocktakes/${id}/` : `/reconciliation/sessions/${id}`;
  return apiFetch<RawReconSession>(path);
}

/** The raw backend stocktake session shape (with frozen count rows). */
export interface RawReconSession {
  id: string;
  reference: string;
  scope: string;
  session_type?: ReconSessionType;
  period_start?: string | null;
  period_end?: string | null;
  status: string;
  store_id?: string;
  counts: Array<{
    batch_stock: string;
    sku: string;
    batch_number: string;
    expiry_date?: string | null;
    bin_location?: string | null;
    expected_qty: number;
    /** Live book stock (current on-hand) = snapshot + activity. */
    book_qty?: number;
    /** Net movement per source during the open period (background count). */
    activity?: ReconActivity;
    counted_qty: number | null;
    variance: number;
    resolution: string;
  }>;
}

/** Net stock movement per source while the reconciliation was open. */
export interface ReconActivity {
  inward: number;
  outward: number;
  transfer_in: number;
  transfer_out: number;
  adjustment: number;
  direct_import: number;
  net: number;
}

/** Record one counted batch in a live session (server applies the DR-002 clamp). */
export function useRecordStocktakeCount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sessionId,
      batchId,
      counted,
    }: {
      sessionId: string;
      batchId: string;
      counted: number;
    }) => {
      if (!useRealApi) return Promise.resolve(null); // mock counts stay client-side
      return apiFetch<RawReconSession>(`/api/inventory/stocktakes/${sessionId}/count/`, {
        method: 'POST',
        body: { batch_stock_id: batchId, counted_qty: counted },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: inventoryKeys.reconSessions() }),
  });
}

export function useCloseReconSession() {
  const qc = useQueryClient();
  const { store } = useStore();
  return useMutation({
    mutationFn: (id: string) => {
      if (useRealApi) {
        return apiFetch<ReconSession>(`/api/inventory/stocktakes/${id}/close/`, {
          method: 'POST',
          body: {},
        });
      }
      return apiFetch<ReconSession>(`/reconciliation/sessions/${id}/close`, { method: 'POST' });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: inventoryKeys.reconSessions() });
      qc.invalidateQueries({ queryKey: inventoryKeys.adjustments(store) });
      // Positive stocktake variances fulfil open owings server-side — refresh the register.
      qc.invalidateQueries({ queryKey: ['stocks', 'backorders'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

/** Soft-delete a reconciliation session (removes it from the list; keeps the WORM ledger/audit). */
export function useDeleteReconSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (!useRealApi) return Promise.resolve(null);
      return apiFetch<null>(`/api/inventory/stocktakes/${id}/`, { method: 'DELETE' });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: inventoryKeys.reconSessions() }),
  });
}

/** Acknowledge / resolve a derived alert (persisted server-side in real mode). */
export function useSetAlertState() {
  const qc = useQueryClient();
  const { store } = useStore();
  return useMutation({
    mutationFn: (body: {
      alertKey: string;
      storeId: string;
      status: 'acknowledged' | 'resolved';
    }) => {
      if (!useRealApi) return Promise.resolve(null); // mock alerts keep page-local state
      return apiFetch<unknown>('/api/inventory/alerts/state/', {
        method: 'POST',
        body: { alert_key: body.alertKey, store_id: body.storeId, status: body.status },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: inventoryKeys.alerts(store) }),
  });
}

/** Move a batch to another bin (audited). */
export function useBatchMove() {
  const qc = useQueryClient();
  const { store, me, role } = useStore();
  return useMutation({
    mutationFn: ({ id, bin }: { id: string; bin: string }) => {
      if (useRealApi) {
        return apiFetch<unknown>(`/api/inventory/batches/${id}/move/`, {
          method: 'POST',
          body: { bin_location: bin },
        });
      }
      return apiFetch<unknown>(`/batches/${id}/move`, {
        method: 'POST',
        body: { bin, who: me, role },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: inventoryKeys.batches(store) }),
  });
}

export function useStores() {
  return useListQuery<{ id: string; name: string }>('/stores', inventoryKeys.stores(), {
    map: useRealApi ? (raw) => unwrapPage(raw).map(storeDtoToStore) : undefined,
  });
}

export function useCreateBin() {
  const qc = useQueryClient();
  const { store } = useStore();
  return useMutation({
    mutationFn: (bin: Partial<Bin>) => {
      if (useRealApi) {
        const path = resolvePath('/bins')?.path ?? '/bins';
        return apiFetch<Bin>(path, { method: 'POST', body: binToStorageBinPayload(bin, store) });
      }
      return apiFetch<Bin>('/bins', { method: 'POST', body: bin });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: inventoryKeys.bins(store) }),
  });
}

export function useCreateProduct() {
  const qc = useQueryClient();
  const { store } = useStore();
  return useMutation({
    mutationFn: (product: Partial<Product>) => {
      if (useRealApi) {
        const path = resolvePath('/products')?.path ?? '/products';
        return apiFetch<Product>(path, { method: 'POST', body: productToCatalogPayload(product) });
      }
      return apiFetch<Product>('/products', { method: 'POST', body: product });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: inventoryKeys.products(store) });
    },
  });
}

/** Patch a product (archive toggle, threshold edit). */
export function useUpdateProduct() {
  const qc = useQueryClient();
  const { store } = useStore();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Product> }) => {
      if (useRealApi) {
        const path = resolveDetailPath(`/products/${id}`);
        if (!path) return Promise.reject(new Error('Path not mapped'));
        return apiFetch<Product>(path, { method: 'PATCH', body: productToCatalogPayload(patch) });
      }
      return apiFetch<Product>(`/products/${id}`, { method: 'PATCH', body: patch });
    },
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: inventoryKeys.products(store) });
      if (updated) qc.invalidateQueries({ queryKey: inventoryKeys.product(updated.id) });
      qc.invalidateQueries({ queryKey: inventoryKeys.alerts(store) });
    },
  });
}

/** Example mutation: create a stock adjustment (optimistic-friendly). */
/** UI reason labels → backend reason codes (shared with the Stocks module). */
const INV_REASON_CODE: Record<string, string> = {
  Damage: 'damage',
  'Expired stock': 'expiry',
  Expiry: 'expiry',
  Theft: 'theft',
  'Count correction': 'stocktake_variance',
  'Found stock': 'stocktake_variance',
  Recall: 'recall',
  'Supplier return': 'return_to_supplier',
};

export function useCreateAdjustment() {
  const qc = useQueryClient();
  const { store } = useStore();
  return useMutation({
    mutationFn: (adjustment: Partial<Adjustment>) => {
      if (useRealApi) {
        return apiFetch<Adjustment>('/api/adjustments/', {
          method: 'POST',
          body: {
            store_id: adjustment.store,
            batch_stock_id: adjustment.batch,
            qty_delta: adjustment.delta,
            reason_code: INV_REASON_CODE[adjustment.reason ?? ''] ?? 'discrepancy',
            evidence_ref: adjustment.comment ?? '',
          },
        });
      }
      return apiFetch<Adjustment>('/adjustments', { method: 'POST', body: adjustment });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: inventoryKeys.adjustments(store) });
      qc.invalidateQueries({ queryKey: inventoryKeys.alerts(store) });
    },
  });
}

/** Approve or reject a pending adjustment (approval applies the stock delta). */
export function useDecideAdjustment() {
  const qc = useQueryClient();
  const { store } = useStore();
  return useMutation({
    mutationFn: async ({ id, decision }: { id: string; decision: 'Approved' | 'Rejected' }) => {
      if (!useRealApi) {
        return apiFetch<Adjustment>(`/adjustments/${id}/decision`, {
          method: 'POST',
          body: { decision },
        });
      }
      const decided = await apiFetch<unknown>(`/api/adjustments/${id}/approve/`, {
        method: 'POST',
        body: { decision: decision === 'Approved' ? 'approve' : 'reject' },
      });
      if (decision !== 'Approved') return decided;
      return apiFetch<unknown>(`/api/adjustments/${id}/apply/`, {
        method: 'POST',
        body: {},
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: inventoryKeys.adjustments(store) });
      qc.invalidateQueries({ queryKey: inventoryKeys.alerts(store) });
      // Approving+applying a positive adjustment fulfils open owings server-side.
      qc.invalidateQueries({ queryKey: ['stocks', 'backorders'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

/** One-shot manual stock correction — set a batch's on-hand from the product page. Applies
 *  immediately (no approval queue) and is recorded in the movement ledger + audit log. */
export interface ManualAdjustParams {
  store_id: string;
  batch_stock_id: string;
  new_qty: number;
  reason_code: string;
  note?: string;
}
export function useManualAdjust() {
  const qc = useQueryClient();
  const { store } = useStore();
  return useMutation({
    mutationFn: (p: ManualAdjustParams) =>
      apiFetch<{ new_on_hand: number; qty_delta: number }>('/api/adjustments/manual-adjust/', {
        method: 'POST',
        headers: { 'Idempotency-Key': crypto.randomUUID() },
        body: p,
      }),
    onSuccess: () => {
      // Refresh every inventory surface (products, batches, movements, audit) + adjustment lists.
      qc.invalidateQueries({ queryKey: ['inventory'] });
      qc.invalidateQueries({ queryKey: inventoryKeys.adjustments(store) });
      qc.invalidateQueries({ queryKey: inventoryKeys.alerts(store) });
      // A positive adjustment fulfils open owings server-side — refresh the
      // backorder register so its status/free-stock reflects the new stock.
      qc.invalidateQueries({ queryKey: ['stocks', 'backorders'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

/** Edit a batch's number / bin / quantity — a detach→attach move recorded in the ledger + audit. */
export interface EditBatchParams {
  id: string;
  batch_number?: string;
  bin_location?: string;
  new_qty?: number;
  expiry_date?: string;
  note?: string;
}
export function useEditBatch() {
  const qc = useQueryClient();
  const { store } = useStore();
  return useMutation({
    mutationFn: ({ id, ...body }: EditBatchParams) =>
      apiFetch<{ moved: boolean }>(`/api/inventory/batches/${id}/edit/`, {
        method: 'POST',
        headers: { 'Idempotency-Key': crypto.randomUUID() },
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory'] });
      qc.invalidateQueries({ queryKey: inventoryKeys.adjustments(store) });
      qc.invalidateQueries({ queryKey: inventoryKeys.alerts(store) });
      // A quantity increase fulfils open owings server-side — refresh the register.
      qc.invalidateQueries({ queryKey: ['stocks', 'backorders'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

/** Flag / unflag a batch as excess ('not required') — offered to other stores in the group. */
export function useToggleExcess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isExcess, note }: { id: string; isExcess: boolean; note?: string }) =>
      apiFetch<unknown>(`/api/inventory/batches/${id}/toggle-excess/`, {
        method: 'POST',
        body: { is_excess: isExcess, note: note ?? '' },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory'] });
      qc.invalidateQueries({ queryKey: ['transfers', 'excess'] });
    },
  });
}

/** Hold / quarantine a batch (blocks free stock) or release it — audited. */
export function useBatchHold() {
  const qc = useQueryClient();
  const { store, me, role } = useStore();
  return useMutation({
    mutationFn: ({
      id,
      mode,
      note,
    }: {
      id: string;
      mode: 'Hold' | 'Quarantine' | 'Release';
      note: string;
    }) => {
      if (useRealApi) {
        const action = mode === 'Release' ? 'release' : 'hold';
        return apiFetch<unknown>(`/api/inventory/batches/${id}/${action}/`, {
          method: 'POST',
          body: { reason: note },
        });
      }
      const path = mode === 'Release' ? `/batches/${id}/release` : `/batches/${id}/hold`;
      return apiFetch<unknown>(path, { method: 'POST', body: { mode, note, who: me, role } });
    },
    onSuccess: () => {
      // Blanket like the sibling batch writes: a hold changes free/blocked
      // everywhere (batches, alerts, store-products, dashboard, network stock).
      qc.invalidateQueries({ queryKey: ['inventory'] });
      qc.invalidateQueries({ queryKey: ['mst'] });
      qc.invalidateQueries({ queryKey: ['transfers', 'excess'] });
      // Releasing quarantine fulfils open owings server-side — refresh the register.
      qc.invalidateQueries({ queryKey: ['stocks', 'backorders'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}
